import React, { useState } from 'react';
import MaterialIcon from '../ui/MaterialIcon';

function SmartSavingsCard({ savings, onViewStrategy }) {
  const [expanded, setExpanded] = useState(true);

  if (!savings || savings.totalSavings <= 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-card border border-outline-variant/8 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-container-low/30 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-success/10 flex items-center justify-center">
            <MaterialIcon name="savings" size={20} className="text-success" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-on-surface">Smart Savings</p>
            <p className="text-xs text-success font-semibold">
              Save up to ${Math.round(savings.totalSavings)}
            </p>
          </div>
        </div>
        <MaterialIcon
          name={expanded ? 'expand_less' : 'expand_more'}
          size={20}
          className="text-on-surface-variant"
        />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <div className="h-px bg-outline-variant/15" />

          {/* Best Card to Pay */}
          {savings.bestCard && (
            <SavingsRow
              icon="credit_card"
              iconBg="bg-primary/10"
              iconColor="text-primary"
              title="Best Card to Pay"
              detail={`${savings.bestCard.cardName} · ${savings.bestCard.earnRate} on travel`}
              value={`+${savings.bestCard.pointsEarned.toLocaleString()} pts`}
              valueColor="text-primary"
            />
          )}

          {/* Loyalty Match */}
          {savings.loyaltyMatch && (
            <SavingsRow
              icon="loyalty"
              iconBg="bg-warning/10"
              iconColor="text-warning"
              title="Loyalty Program"
              detail={`${savings.loyaltyMatch.programName} · ${savings.loyaltyMatch.currentBalance.toLocaleString()} ${savings.loyaltyMatch.canRedeem ? '(enough to redeem!)' : 'miles'}`}
              value={savings.loyaltyMatch.canRedeem ? 'Redeemable' : `Need ${(savings.loyaltyMatch.milesNeeded - savings.loyaltyMatch.currentBalance).toLocaleString()} more`}
              valueColor={savings.loyaltyMatch.canRedeem ? 'text-success' : 'text-on-surface-variant'}
            />
          )}

          {/* Transfer Opportunities */}
          {savings.transferOpportunities.map((opp, i) => (
            <SavingsRow
              key={i}
              icon="sync_alt"
              iconBg="bg-info/10"
              iconColor="text-info"
              title={`Transfer ${opp.bank} → ${opp.airline}`}
              detail={`${opp.ratio} ratio${opp.bonus ? ` · ${opp.bonus}` : ''}`}
              value={`~$${opp.estimatedValue}`}
              valueColor="text-success"
            />
          ))}

          {/* Travel Credits */}
          {savings.travelCredits.filter(c => c.applicable).map((credit, i) => (
            <SavingsRow
              key={`credit-${i}`}
              icon="card_giftcard"
              iconBg="bg-success/10"
              iconColor="text-success"
              title="Travel Credit"
              detail={`${credit.card} · ${credit.credit}`}
              value="Applicable"
              valueColor="text-success"
            />
          ))}

          {/* View Strategy Button */}
          {onViewStrategy && savings.strategies.length > 0 && (
            <button
              onClick={onViewStrategy}
              className="w-full mt-1 py-2.5 rounded-xl bg-primary/5 text-primary text-sm font-semibold hover:bg-primary/10 transition-colors flex items-center justify-center gap-1.5"
            >
              <MaterialIcon name="route" size={16} className="text-primary" />
              View Full Strategy
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SavingsRow({ icon, iconBg, iconColor, title, detail, value, valueColor }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <MaterialIcon name={icon} size={16} className={iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-on-surface">{title}</p>
        <p className="text-[11px] text-on-surface-variant truncate">{detail}</p>
      </div>
      <span className={`text-xs font-semibold ${valueColor} flex-shrink-0`}>{value}</span>
    </div>
  );
}

export default SmartSavingsCard;
